/**
 * Template-specific utility functions
 * Extracted from AddCanvasTemplate.js to reduce file size
 */
import { convertColorValue } from "./imageUploadUtils";
import { openCanvasDB } from "../../../util/indexedDBUtils";

/**
 * Generate snapshot from API response elements
 * @param {Object} elements - Elements containing shapes and images from API response
 * @returns {Promise<string>} Base64 encoded image data URL
 */
export const generateSnapshotFromResponse = async (elements) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = 800;
  canvas.height = 600;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const imageDrawingPromises = [];

  const shapes = elements.shapes || [];

  for (const shape of shapes) {
    const info = shape.shape_info || {};
    const shapeProperties = shape.shape_properties || {};
    const locationAndSize = shape.canvas_page_location_and_size || {};
    const type = shape.type;

    switch (type) {
      case "circle": {
        const x = locationAndSize.x || info.x || shapeProperties.x || 0;
        const y = locationAndSize.y || info.y || shapeProperties.y || 0;
        const radius =
          info.radius ||
          shapeProperties.radius ||
          Math.min(
            locationAndSize.width || 100,
            locationAndSize.height || 100
          ) / 2;
        const fill = convertColorValue(
          info.fill ||
            shapeProperties.fill ||
            shapeProperties.color ||
            "#FFFFFF"
        );
        const stroke = convertColorValue(
          info.stroke ||
            shapeProperties.stroke ||
            shapeProperties.color ||
            "#000"
        );
        const fillOpacity = 1;
        const strokeOpacity = 1;

        ctx.beginPath();
        ctx.arc(x, y, radius, 0, 2 * Math.PI);

        // Fill (usually white background)
        ctx.fillStyle = convertColorValue(fill);
        ctx.globalAlpha = fillOpacity;
        ctx.fill();

        // Stroke (colored border)
        ctx.strokeStyle = convertColorValue(stroke);
        ctx.globalAlpha = strokeOpacity;
        ctx.stroke();

        // Reset globalAlpha to avoid affecting later shapes
        ctx.globalAlpha = 1;

        break;
      }

      case "rectangle": {
        const x = locationAndSize.x || info.x || shapeProperties.x || 0;
        const y = locationAndSize.y || info.y || shapeProperties.y || 0;
        const width =
          locationAndSize.width || info.width || shapeProperties.width || 100;
        const height =
          locationAndSize.height ||
          info.height ||
          shapeProperties.height ||
          100;
        const fill = convertColorValue(
          info.fill ||
            shapeProperties.fill ||
            shapeProperties.color ||
            "#FFFFFF"
        );
        const stroke = convertColorValue(
          info.stroke ||
            shapeProperties.stroke ||
            shapeProperties.color ||
            "#000"
        );
        const fillOpacity = 1;
        const strokeOpacity = 1;
        const strokeWidth =
          info.strokeWidth ||
          shapeProperties.strokeWidth ||
          shapeProperties.borderWidth ||
          1;

        // Fill first
        ctx.fillStyle = convertColorValue(fill);
        ctx.globalAlpha = fillOpacity;
        ctx.fillRect(x, y, width, height);

        // Stroke (border)
        if (stroke && strokeOpacity > 0 && strokeWidth > 0) {
          ctx.globalAlpha = strokeOpacity;
          ctx.lineWidth = strokeWidth;
          ctx.strokeStyle = convertColorValue(stroke);
          ctx.strokeRect(x, y, width, height);
        }

        // Reset global alpha
        ctx.globalAlpha = 1;

        break;
      }

      case "lines": {
        const points = info.points ||
          shapeProperties.points || [0, 0, 100, 100];
        const stroke = convertColorValue(
          info.strokeColor ||
            info.stroke ||
            shapeProperties.stroke ||
            shapeProperties.color ||
            "#000"
        );
        const strokeWidth =
          info.strokeWidth ||
          shapeProperties.strokeWidth ||
          shapeProperties.borderWidth ||
          2;
        const startDot = info.startDot || shapeProperties.startDot;
        const endDot = info.endDot || shapeProperties.endDot;
        const x = locationAndSize.x || info.x || shapeProperties.x || 0;
        const y = locationAndSize.y || info.y || shapeProperties.y || 0;

        if (points.length >= 4) {
          const [x1, y1, x2, y2] = points;

          // Draw the line
          ctx.beginPath();
          ctx.moveTo(x + x1, y + y1);
          ctx.lineTo(x + x2, y + y2);
          ctx.strokeStyle = convertColorValue(stroke);
          ctx.lineWidth = strokeWidth;
          ctx.stroke();

          // Draw startDot
          if (startDot?.radius && startDot?.fill) {
            ctx.beginPath();
            ctx.arc(x + x1, y + y1, startDot.radius, 0, 2 * Math.PI);
            ctx.fillStyle = convertColorValue(startDot.fill);
            ctx.fill();
            if (startDot.stroke && startDot.strokeWidth) {
              ctx.lineWidth = startDot.strokeWidth;
              ctx.strokeStyle = convertColorValue(startDot.stroke);
              ctx.stroke();
            }
          }

          // Draw endDot
          if (endDot?.radius && endDot?.fill) {
            ctx.beginPath();
            ctx.arc(x + x2, y + y2, endDot.radius, 0, 2 * Math.PI);
            ctx.fillStyle = convertColorValue(endDot.fill);
            ctx.fill();
            if (endDot.stroke && endDot.strokeWidth) {
              ctx.lineWidth = endDot.strokeWidth;
              ctx.strokeStyle = convertColorValue(endDot.stroke);
              ctx.stroke();
            }
          }
        }

        break;
      }
      case "arrow": {
        const points = info.points ||
          shapeProperties.points || [0, 0, 100, 100];
        const stroke = convertColorValue(
          info.strokeColor ||
            info.stroke ||
            shapeProperties.stroke ||
            shapeProperties.color ||
            "#000"
        );
        const strokeWidth =
          info.strokeWidth ||
          shapeProperties.strokeWidth ||
          shapeProperties.borderWidth ||
          2;
        const x = locationAndSize.x || info.x || shapeProperties.x || 0;
        const y = locationAndSize.y || info.y || shapeProperties.y || 0;

        if (points.length >= 4) {
          const [x1, y1, x2, y2] = points;

          // Line
          ctx.beginPath();
          ctx.moveTo(x + x1, y + y1);
          ctx.lineTo(x + x2, y + y2);
          ctx.strokeStyle = convertColorValue(stroke);
          ctx.lineWidth = strokeWidth;
          ctx.stroke();

          // OPTIONAL: Arrowhead
          const angle = Math.atan2(y2 - y1, x2 - x1);
          const headLength = 10; // Length of arrowhead

          const arrowX = x + x2;
          const arrowY = y + y2;

          ctx.beginPath();
          ctx.moveTo(arrowX, arrowY);
          ctx.lineTo(
            arrowX - headLength * Math.cos(angle - Math.PI / 6),
            arrowY - headLength * Math.sin(angle - Math.PI / 6)
          );
          ctx.moveTo(arrowX, arrowY);
          ctx.lineTo(
            arrowX - headLength * Math.cos(angle + Math.PI / 6),
            arrowY - headLength * Math.sin(angle + Math.PI / 6)
          );
          ctx.stroke();
        }

        break;
      }

      case "double_sided_arrow": {
        const points = info.points ||
          shapeProperties.points || [0, 0, 100, 100];
        const stroke = convertColorValue(
          info.strokeColor ||
            info.stroke ||
            shapeProperties.stroke ||
            shapeProperties.color ||
            "#000"
        );
        const strokeWidth =
          info.strokeWidth ||
          shapeProperties.strokeWidth ||
          shapeProperties.borderWidth ||
          2;
        const x = locationAndSize.x || info.x || shapeProperties.x || 0;
        const y = locationAndSize.y || info.y || shapeProperties.y || 0;

        if (points.length >= 4) {
          const [x1, y1, x2, y2] = points;

          ctx.beginPath();
          ctx.moveTo(x + x1, y + y1);
          ctx.lineTo(x + x2, y + y2);
          ctx.strokeStyle = convertColorValue(stroke);
          ctx.lineWidth = strokeWidth;
          ctx.stroke();

          const drawArrowhead = (fromX, fromY, toX, toY) => {
            const angle = Math.atan2(toY - fromY, toX - fromX);
            const headLength = 10;

            ctx.beginPath();
            ctx.moveTo(toX, toY);
            ctx.lineTo(
              toX - headLength * Math.cos(angle - Math.PI / 6),
              toY - headLength * Math.sin(angle - Math.PI / 6)
            );
            ctx.moveTo(toX, toY);
            ctx.lineTo(
              toX - headLength * Math.cos(angle + Math.PI / 6),
              toY - headLength * Math.sin(angle + Math.PI / 6)
            );
            ctx.stroke();
          };

          // Draw arrowhead at both ends
          drawArrowhead(x + x2, y + y2, x + x1, y + y1); // head at end
          drawArrowhead(x + x1, y + y1, x + x2, y + y2); // head at start
        }

        break;
      }

      case "text": {
        const x = locationAndSize.x || info.x || shapeProperties.x || 0;
        const y = locationAndSize.y || info.y || shapeProperties.y || 0;
        const content = info.content || shapeProperties.content;
        const plainText =
          info.plainText || shapeProperties.plainText || shape.rich_text || "";
        const fontSize = info.fontSize || shapeProperties.fontSize || 18;
        const fontFamily =
          info.fontFamily || shapeProperties.fontFamily || "Arial";
        const fillColor = convertColorValue(
          info.fillColor ||
            info.fill ||
            shapeProperties.fill ||
            shapeProperties.color ||
            "#000"
        );
        ctx.font = `${fontSize}px ${fontFamily}`;
        ctx.fillStyle = convertColorValue(fillColor);
        ctx.fillText(plainText || content || "", x, y);
        break;
      }
      case "image": {
        const x =
          locationAndSize.x || shape.x || info.x || shapeProperties.x || 0;
        const y =
          locationAndSize.y || shape.y || info.y || shapeProperties.y || 0;
        const width =
          locationAndSize.width ||
          shape.width ||
          info.width ||
          shapeProperties.width ||
          100;
        const height =
          locationAndSize.height ||
          shape.height ||
          info.height ||
          shapeProperties.height ||
          100;

        const src =
          shape.originalImageDataUrl ||
          shape.imageDataUrl ||
          info.imageDataUrl ||
          shape.src ||
          shape.image_path ||
          "";

        if (!src || src.startsWith("blob:")) {
          break;
        }

        const img = new Image();
        const promise = new Promise((resolve) => {
          img.onload = () => {
            ctx.drawImage(img, x, y, width, height);

            // Create an offscreen canvas to extract base64 version of this image
            const tempCanvas = document.createElement("canvas");
            tempCanvas.width = width;
            tempCanvas.height = height;
            const tempCtx = tempCanvas.getContext("2d");
            tempCtx.drawImage(img, 0, 0, width, height);

            const base64DataUrl = tempCanvas.toDataURL("image/png");

            // Attach the base64 back to the shape for future use
            shape.generatedImageDataUrl = base64DataUrl;

            resolve();
          };
          img.onerror = (err) => {
            resolve(); // Don't block snapshot generation
          };
          img.src = src;
        });

        imageDrawingPromises.push(promise);
        break;
      }

      default:
    }
  }

  await Promise.all(imageDrawingPromises);

  // Also process images from the images array if they exist
  if (elements.images && Array.isArray(elements.images)) {
    for (const img of elements.images) {
      const imageProperties = img.image_properties || {};
      const locationAndSize = img.canvas_page_location_and_size || {};

      const x = locationAndSize.x || imageProperties.x || 0;
      const y = locationAndSize.y || imageProperties.y || 0;
      const width = locationAndSize.width || imageProperties.width || 100;
      const height = locationAndSize.height || imageProperties.height || 100;
      const rotation =
        locationAndSize.rotation || imageProperties.rotation || 0;
      const src = img.image_path || img.src || "";

      if (src && !src.startsWith("blob:")) {
        const imgElement = new Image();
        const promise = new Promise((resolve) => {
          imgElement.onload = () => {
            ctx.save();
            // Translate to image center for rotation
            ctx.translate(x + width / 2, y + height / 2);
            // Apply rotation if needed
            if (rotation !== 0) {
              ctx.rotate((rotation * Math.PI) / 180);
            }
            // Draw image centered at origin
            ctx.drawImage(imgElement, -width / 2, -height / 2, width, height);
            ctx.restore();
            resolve();
          };
          imgElement.onerror = (err) => {
            resolve();
          };
          imgElement.src = src;
        });
        imageDrawingPromises.push(promise);
      }
    }
  }

  await Promise.all(imageDrawingPromises);

  return canvas.toDataURL("image/png");
};

/**
 * Get markers from IndexedDB
 * @returns {Promise<Array>} Array of marker objects
 */
export const getMarkersFromIndexedDB = async () => {
  return new Promise(async (resolve) => {
    try {
      const db = await openCanvasDB();
      if (!db.objectStoreNames.contains("canvasMarkers")) {
        resolve([]);
        return;
      }
      const transaction = db.transaction(["canvasMarkers"], "readonly");
      const store = transaction.objectStore("canvasMarkers");
      const getRequest = store.get("currentMarkers");
      getRequest.onsuccess = (event) => {
        const markers = event.target.result?.data || [];
        resolve(markers);
      };
      getRequest.onerror = () => resolve([]);
    } catch (e) {
      resolve([]);
    }
  });
};

/**
 * Generate layout configuration for canvas template
 * @param {Object} elements - Canvas elements (texts, images, rectangles, etc.)
 * @returns {Object} Layout configuration with two-column structure
 */
export const generateLayoutConfig = (elements) => {
  const leftColumnElements = [];
  const rightColumnElements = [];

  const assignToColumn = (el) => {
    const x = el.x || 0;
    return x < 230 ? leftColumnElements : rightColumnElements;
  };

  // Texts
  (elements.texts || []).forEach((text) => {
    assignToColumn(text).push({
      id: text.id,
      type: "text_box",
      position: { x: text.x, y: text.y },
      size: { width: text.width || 200, height: text.height || 100 },
      text: text.text || "",
      style: {
        fontSize: text.fontSize || 14,
        fontWeight: text.fontStyle?.includes("bold") ? "bold" : "normal",
        color: text.fill || "#000000",
      },
    });
  });

  // Images
  (elements.images || []).forEach((img) => {
    assignToColumn(img).push({
      id: img.id,
      type: "Image",
      position: { x: img.x, y: img.y },
      size: { width: img.width, height: img.height },
    });
  });

  // Rectangles
  (elements.rectangles || []).forEach((rect) => {
    assignToColumn(rect).push({
      id: rect.id,
      type: "rectangle",
      position: { x: rect.x, y: rect.y },
      size: { width: rect.width, height: rect.height },
      style: {
        fillColor: rect.fillColor || "#000000",
      },
    });
  });

  // Circles
  (elements.circles || []).forEach((circle) => {
    assignToColumn(circle).push({
      id: circle.id,
      type: "circle",
      position: { x: circle.x, y: circle.y },
      radius: circle.radius,
      style: {
        fillColor: circle.fillColor || "#000000",
      },
    });
  });

  // Arrows
  (elements.arrows || []).forEach((arrow) => {
    assignToColumn(arrow).push({
      id: arrow.id,
      type: "arrow",
      points: arrow.points || [],
      style: {
        stroke: arrow.stroke || "#000000",
        strokeWidth: arrow.strokeWidth || 2,
      },
    });
  });

  // Capture canvas/page height from editor if present; default to 100vh
  // The editor stores canvasHeight (numeric vh) with elements in IndexedDB; include it in layout_config
  const pageHeightVh = parseInt(elements?.canvasHeight) || 100;

  return {
    layout: {
      type: "two_column",
      page_height_vh: pageHeightVh,
      columns: [
        {
          id: "left_column",
          width: "50%",
          elements: leftColumnElements,
        },
        {
          id: "right_column",
          width: "50%",
          elements: rightColumnElements,
        },
      ],
    },
  };
};

/**
 * Save canvas elements to IndexedDB for compatibility with MyCanvas
 * @param {Object} elements - Canvas elements to save
 * @param {string} pageId - Page identifier
 * @param {Function} convertBlobToBase64 - Function to convert blob URLs to base64
 */
export const saveCanvasElementsToIndexedDB = async (
  elements,
  pageId,
  convertBlobToBase64
) => {
  if (!window.indexedDB) {
    return;
  }

  if (!pageId) {
    return;
  }

  try {
    // Open the database using shared helper without forcing version upgrades
    const db = await openCanvasDB();

    const existingElements = await new Promise((resolve, reject) => {
      const tx = db.transaction(["canvasElements"], "readonly");
      const store = tx.objectStore("canvasElements");
      const getReq = store.get(pageId);
      getReq.onsuccess = () => resolve(getReq.result);
      getReq.onerror = (event) => reject(event.target.error);
    });

    const stateInfo = sessionStorage.getItem("canvasStateInfo");
    const hasTextUpdates = stateInfo
      ? JSON.parse(stateInfo)?.includesText
      : false;

    // Normalize image sources to base64 (avoid blob URLs)
    const normalizedImages = [];
    for (const img of elements.images || []) {
      try {
        let src =
          img.imgDataUrl ||
          img.originalImageDataUrl ||
          img.image_path ||
          img.src;
        if (src && src.startsWith("blob:")) {
          const b64 = await convertBlobToBase64(src);
          if (b64) src = b64;
        }
        normalizedImages.push({
          ...img,
          id: img.id || img.image_id || img.shape_id || Date.now().toString(),
          x: img.x || 50,
          y: img.y || 50,
          width: img.width || 100,
          height: img.height || 100,
          src,
        });
      } catch (e) {
        normalizedImages.push({
          ...img,
          id: img.id || img.image_id || img.shape_id || Date.now().toString(),
          x: img.x || 50,
          y: img.y || 50,
          width: img.width || 100,
          height: img.height || 100,
          src: img.src,
        });
      }
    }

    // Merge existing and new images, then dedupe by stable key (id/shape_id/image_id/src)
    const combinedImages = [
      ...(existingElements?.images || []),
      ...normalizedImages,
    ];
    const imageKey = (img) =>
      img?.id || img?.shape_id || img?.image_id || img?.src || "";
    const imagesDeduped = Array.from(
      new Map(combinedImages.map((im) => [imageKey(im), im])).values()
    );

    const elementsToStore = {
      id: pageId,
      texts:
        hasTextUpdates && existingElements?.texts
          ? existingElements.texts
          : elements.texts || existingElements?.texts || [],
      rectangles: elements.rectangles || existingElements?.rectangles || [],
      circles: elements.circles || existingElements?.circles || [],
      arrows: elements.arrows || existingElements?.arrows || [],
      scribbles: elements.scribbles || existingElements?.scribbles || [],
      lines: elements.lines || existingElements?.lines || [],
      doubleSidedArrows:
        elements.doubleSidedArrows || existingElements?.doubleSidedArrows || [],
      images: imagesDeduped,
      // Preserve snapshot if provided
      ...(elements.snapshot
        ? { snapshot: elements.snapshot }
        : existingElements?.snapshot
        ? { snapshot: existingElements.snapshot }
        : {}),
      // Preserve canva_snap_url if provided (similar to EditCanvasPage)
      ...(elements.canva_snap_url || existingElements?.canva_snap_url
        ? {
            canva_snap_url:
              elements.canva_snap_url || existingElements.canva_snap_url,
          }
        : {}),
      lastModified: new Date().toISOString(),
    };

    await new Promise((resolve, reject) => {
      const tx = db.transaction(["canvasElements"], "readwrite");
      const store = tx.objectStore("canvasElements");
      const putReq = store.put(elementsToStore);
      putReq.onsuccess = () => resolve();
      putReq.onerror = (event) => reject(event.target.error);
    });
  } catch (error) {
    // Silently fail - IndexedDB errors are handled gracefully
  }
};
